import {
  AssignDeviceException,
  FEATURES,
  IGroupInfo,
  IRPMDevice,
  RPM_DEVICE_ACTION,
} from "@home-health-notify/domain";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import {
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { ChatContext, OrganizationContext, useTranslation } from "hhn-commons";
import { useSnackbar } from "notistack";
import { useContext, useState } from "react";
import AssignedDeviceItem from "./AssignedDeviceItem";
import ForceAssignDeviceDialog from "./ForceAssignDeviceDialog";
import InputDeviceId from "./InputDeviceId";
import {
  ActionsContainer,
  Header,
  LoaderContainer,
  Title,
  VitalsContainer,
  VitalsIconButton,
} from "./Vitals.style";

export const Vitals = ({ groupInfo }: { groupInfo: IGroupInfo }) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "groupChatInfo" });
  const { chat } = useContext(ChatContext);
  const { selectedOrgUser } = useContext(OrganizationContext);
  const canManageDevices = !!selectedOrgUser?.hasAccess(
    FEATURES.MANAGE_RPM_DEVICES,
  );
  const canExportReadings = !!selectedOrgUser?.hasAccess(
    FEATURES.EXPORT_RPM_READINGS,
  );
  const { rpmDevices } = groupInfo;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [isAddMode, setIsAddMode] = useState<boolean>(false);
  const [deviceId, setDeviceId] = useState<string>("");
  const [isExportingReadings, setIsExportingReadings] =
    useState<boolean>(false);

  const [isForceAssignDeviceDialogOpen, setIsForceAssignDeviceOpen] =
    useState<boolean>(false);
  const [forceAssignDialogTitle, setForceAssignDialogTitle] =
    useState<string>("");
  const [forceAssignDialogMessage, setForceAssignDialogMessage] =
    useState<string>("");
  const [forceAssignHandler, setForceAssignHandler] = useState<() => void>();

  const openForceAssignDialog = (deviceId: string, message: string) => {
    setForceAssignDialogTitle(tNs("assignDevices.unableToAssignDevice"));
    setForceAssignDialogMessage(
      `( ${tNs("assignDevices.alertDeviceId")} ${deviceId} ) : ${tNs("assignDevices." + message)}`,
    );
    setForceAssignHandler(
      () => () =>
        handleManageDevicePatient(deviceId, RPM_DEVICE_ACTION.ASSIGNED, true),
    );
    setIsForceAssignDeviceOpen(true);
  };

  const closeForceAssignDialog = () => {
    setIsForceAssignDeviceOpen(false);
  };

  const mergeRpmDevices = (devices: IRPMDevice[]): IRPMDevice[] => {
    const deviceMap: { [key: string]: IRPMDevice } = (devices || []).reduce(
      (acc, device) => {
        if (acc[device.rpmDeviceType]) {
          acc[device.rpmDeviceType].rpmDeviceId += `, ${device.rpmDeviceId}`;
        } else {
          acc[device.rpmDeviceType] = { ...device };
        }
        return acc;
      },
      {} as { [key: string]: IRPMDevice },
    );

    return Object.values(deviceMap);
  };

  const mergedRpmDevices = mergeRpmDevices(rpmDevices);
  const toggleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  const closeAddMode = () => {
    setIsAddMode(false);
    setDeviceId("");
  };

  const onPressAddDevice = async (deviceId: string) => {
    if (chat && deviceId) {
      await handleManageDevicePatient(deviceId, RPM_DEVICE_ACTION.ASSIGNED);
    }
  };

  const onRemoveDevice = async (removeDeviceId: string) => {
    if (chat && removeDeviceId) {
      await handleManageDevicePatient(
        removeDeviceId,
        RPM_DEVICE_ACTION.REMOVED,
      );
    }
  };

  const getDeviceActionMessage = (
    status: "success" | "failure",
    action: RPM_DEVICE_ACTION,
    deviceId: string,
    errorMessageKey?: string,
  ) => {
    let titleKey = "";

    if (status === "success") {
      titleKey =
        action === RPM_DEVICE_ACTION.ASSIGNED
          ? "deviceAssignedSuccessfully"
          : "deviceRemovedSuccessfully";
    } else {
      titleKey =
        action === RPM_DEVICE_ACTION.ASSIGNED
          ? "unableToAssignDevice"
          : "unableToRemoveDevice";
    }

    const title = titleKey ? tNs("assignDevices." + titleKey) : "";
    const message =
      status === "success"
        ? tNs("assignDevices.rpmDeviceUpdated")
        : errorMessageKey
          ? tNs(`assignDevices.${errorMessageKey}`)
          : tNs("assignDevices.errorManagingDevice");

    return `${title} ( ${tNs("assignDevices.alertDeviceId")} ${deviceId} ) : ${message}`;
  };

  const handleManageDevicePatient = async (
    deviceId: string,
    action: RPM_DEVICE_ACTION,
    forceAssign = false,
  ) => {
    try {
      if (chat && deviceId) {
        await chat.manageDevicePatient({
          rpmDeviceId: deviceId,
          action,
          forceAssign,
        });
        setDeviceId("");
        setIsAddMode(false);
        enqueueSnackbar(getDeviceActionMessage("success", action, deviceId), {
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      if (error instanceof AssignDeviceException) {
        if (error.message === "DEVICE_ALREADY_ASSIGNED") {
          openForceAssignDialog(deviceId, error.message);
        } else {
          enqueueSnackbar(
            getDeviceActionMessage("failure", action, deviceId, error.message),
            { autoHideDuration: 3000 },
          );
        }
      } else {
        enqueueSnackbar(getDeviceActionMessage("failure", action, deviceId), {
          autoHideDuration: 3000,
        });
      }
    }
  };

  const onExportReadings = async () => {
    if (chat) {
      setIsExportingReadings(true);
      enqueueSnackbar("We will notify you when files are ready for download", {
        autoHideDuration: 3000,
      });
      await chat.exportRequest();
      closeSnackbar();
      setIsExportingReadings(false);
      enqueueSnackbar(
        `${chat.chatInfo.name} RPM device export ready for download.`,
        {
          autoHideDuration: 3000,
        },
      );
    }
  };

  return (
    <VitalsContainer>
      <Header>
        {!isAddMode && <Title>{tNs("assignDevice")}</Title>}
        {isAddMode && canManageDevices && (
          <InputDeviceId
            value={deviceId}
            onChangeValue={value => setDeviceId(value)}
            onIconClick={() => onPressAddDevice(deviceId)}
          />
        )}
        <ActionsContainer>
          {canManageDevices &&
            (!isAddMode ? (
              <Tooltip title="Add Device">
                <VitalsIconButton
                  onClick={toggleAddMode}
                  data-testid="add-device"
                >
                  <AddIcon />
                </VitalsIconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Cancel">
                <VitalsIconButton onClick={closeAddMode}>
                  <CloseIcon />
                </VitalsIconButton>
              </Tooltip>
            ))}
          {canExportReadings && (
            <Tooltip title="Export Device Reading">
              {!isExportingReadings ? (
                <IconButton onClick={onExportReadings}>
                  <MonitorHeartIcon />
                </IconButton>
              ) : (
                <LoaderContainer>
                  <CircularProgress size={24} style={{ margin: "auto" }} />
                </LoaderContainer>
              )}
            </Tooltip>
          )}
        </ActionsContainer>
      </Header>
      {mergedRpmDevices.map((rpmDevice, index) => (
        <AssignedDeviceItem
          key={`${rpmDevice.rpmDeviceId}_${index}`}
          rpmDevice={rpmDevice}
          canRemoveDevice={canManageDevices}
          onRemoveDevice={onRemoveDevice}
        />
      ))}
      {mergedRpmDevices.length === 0 && (
        <Typography>No device assigned</Typography>
      )}
      <ForceAssignDeviceDialog
        isOpen={isForceAssignDeviceDialogOpen}
        title={forceAssignDialogTitle}
        message={forceAssignDialogMessage}
        onClose={closeForceAssignDialog}
        onForceAssignDevice={forceAssignHandler || (() => {})}
      />
    </VitalsContainer>
  );
};
